import { useMemo } from "react";
import { z } from "zod";
import {
  useCategoricalColors,
  type CategoricalColors,
  type ChartColorToken,
  type WidgetSettingField,
} from "@salestrak/ui";
import type { MeasureFn } from "@salestrak/widgets";
// Direct module path (not the feature barrel): definitions import this reader,
// and the barrel imports the Dashboard host → the app registry → definitions.
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import {
  WIDGET_DIMENSION_OPTIONS,
  useDimensionCounts,
  useGroupDimension,
  type DimensionSlice,
  type GroupByChoice,
  type WidgetDimension,
} from "#app/widgets/dashboards/use-dimension-counts";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import {
  DIMENSION_FIELDS_SOURCE,
  MEASURE_SETTING_FIELD,
  MISSING_FIELD_LABEL,
  foldMeasure,
  measureFieldId,
  measureSettingFields,
  useFieldMeasures,
  useMeasureFormat,
  useMeasureFormats,
  type FieldMeasure,
  type MeasureFormat,
} from "#app/widgets/dashboards/widget-measure";

/** Dashboards count live work — archived projects are out unless a widget's own
 *  filters opt them back in. THE shared base `where`; every widget data hook
 *  imports this, never a local copy. */
export const ARCHIVED_OUT = { archived: { eq: false } };

/**
 * THE shared reader for every dimension-sliced count widget (pie / bar /
 * battery / treemap): counts projects grouped by the chosen dimension, ranked
 * high→low, over the ambient dashboard window narrowed by the widget's own
 * Filters blob (`useWidgetFilters`: archived-out unless shown, saved query,
 * widget-owned date range). A widget NEVER re-plumbs the scope fold — it calls
 * this and renders the slices however it likes. `measures` are the widget's
 * field measures (`fieldMeasures` in `widget-measure.ts`): empty counts,
 * otherwise the first field's aggregate becomes each slice's `total`. A null
 * `groupBy` (no field chosen yet) asks nothing.
 */
export function useDimensionSlices(
  groupBy: WidgetDimension | null,
  filters?: unknown,
  dateField: "creationDate" | "lastUpdateDate" = "creationDate",
  measures: readonly FieldMeasure[] = [],
  keepUnassigned = false,
) {
  const { base, override } = useWidgetFilters(filters);
  const { where } = useScopedWhere(base, { dateField, override });
  // The `outcome` dimension counts Update activities, not projects, and the
  // activity query takes a different `where` shape entirely — so the activity
  // twin of the fold resolves here too and `useDimensionCounts` picks whichever
  // its dimension needs. The widget's own saved query is issue-shaped, so it
  // stays out of the activity fold; the ambient window, team and actor facets
  // reach both. Activities date on when the Update was posted, so the activity
  // fold keeps its own `creationDate` default rather than the widget's
  // `dateField` (which names an ISSUE column). `subject: "age"` is the Update
  // stream — the same base Update outcomes folds, so both read one
  // population.
  const { where: activityWhere } = useScopedActivityWhere(
    { subject: { eq: "age" } },
    { override },
  );
  return useDimensionCounts(groupBy, where, activityWhere, measures, keepUnassigned);
}

/** The id the folded tail carries. THE one name for it — a widget that wants to
 *  treat the remainder differently from a real category keys on this, never on
 *  the label or a restated literal. */
export const OTHER_SLICE_ID = "__other__";

/** How many slices a widget keeps before the tail folds into a single "Other". */
export type SliceShow = "top5" | "top8" | "top12" | "all";
export type SliceSort = "volume" | "alpha";

/** The shared "Show" select options — one vocabulary across pie / bar / battery. */
export const SLICE_SHOW_OPTIONS: { value: SliceShow; label: string }[] = [
  { value: "top5", label: "Top 5" },
  { value: "top8", label: "Top 8" },
  { value: "top12", label: "Top 12" },
  { value: "all", label: "All groups" },
];

/** The shared "Sort by" select options. */
export const SLICE_SORT_OPTIONS: { value: SliceSort; label: string }[] = [
  // "Largest" rather than "most projects": under a field measure the figure is
  // a sum or an average, and the order is still biggest figure first.
  { value: "volume", label: "Largest first" },
  { value: "alpha", label: "Alphabetical" },
];

/** The shared Group by select: the built-in dimensions, then the workspace's
 *  groupable fields under their type headings, one flat list — searchable,
 *  because the field half is as long as the workspace makes it. */
export const GROUP_BY_SETTING_FIELD: WidgetSettingField = {
  key: "groupBy",
  kind: "select",
  label: "Group by",
  icon: "layers",
  searchable: true,
  missingLabel: MISSING_FIELD_LABEL,
  options: WIDGET_DIMENSION_OPTIONS,
  dynamicOptions: DIMENSION_FIELDS_SOURCE,
};

/** The toggle that keeps the rows carrying no value for the Group by as one
 *  grey slice (`useDimensionCounts`'s `keepUnassigned`). Off by default:
 *  these charts show the categorized work. */
export const SHOW_UNASSIGNED_SETTING_FIELD: WidgetSettingField = {
  key: "showUnassigned",
  kind: "toggle",
  label: "Show Projects without a value",
  icon: "circle-dashed",
};
/** The toggle's config key, `false` unless the widget turned it on. */
export const showUnassignedSchema = z.boolean().default(false);

/** The Group by row. `key` renames the select for a module whose config spells
 *  the dimension differently (`portfolio-map`). */
export function groupBySettingField(key = GROUP_BY_SETTING_FIELD.key): WidgetSettingField {
  return key === GROUP_BY_SETTING_FIELD.key ? GROUP_BY_SETTING_FIELD : { ...GROUP_BY_SETTING_FIELD, key };
}

/** The Show and Sort by selects every slice widget spreads into its Data section. */
export const SLICE_SHAPE_FIELDS: WidgetSettingField[] = [
  { key: "show", kind: "select", label: "Show", icon: "eye", options: SLICE_SHOW_OPTIONS },
  {
    key: "sort",
    kind: "select",
    label: "Sort by",
    icon: "arrow-down-wide-narrow",
    options: SLICE_SORT_OPTIONS,
  },
];

/**
 * The whole Data section of a slice widget for its current config: Group by,
 * Measure (+ Calculate under a field), Show, Sort by — one vocabulary, one
 * icon set, still opt-in per module (a widget with a bespoke Data section
 * builds its own).
 */
export function sliceDataFields(config: SliceConfig): WidgetSettingField[] {
  return [
    groupBySettingField(),
    ...measureSettingFields(MEASURE_SETTING_FIELD, measureFieldId(config.measure) !== null),
    ...SLICE_SHAPE_FIELDS,
  ];
}

/** The config keys the shared slice plumbing reads. Every slice widget's schema
 *  carries them (`groupBySchema`, `measureSchema`, `calculateSchema`; the bar
 *  adds `compareFieldsSchema`). */
export type SliceConfig = {
  groupBy: GroupByChoice;
  measure?: string | undefined;
  calculate?: string | null | undefined;
  compareFields?: readonly string[] | undefined;
  showUnassigned?: boolean | undefined;
};

/** The Gridlines / Axes toggle pair every XY-chart widget spreads into Display. */
export const AXES_DISPLAY_FIELDS: WidgetSettingField[] = [
  { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
  { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
];

const SHOW_LIMIT: Record<SliceShow, number> = {
  top5: 5,
  top8: 8,
  top12: 12,
  all: Number.POSITIVE_INFINITY,
};

/**
 * Fold everything past the chosen limit into one "Other" slice, then apply the
 * display sort — so a pie's wedges, a battery's segments, and a bar's columns
 * all stay a bounded, readable set that still sums to the whole. "Top N" keeps
 * exactly N NAMED slices; "Other" is the extra (N+1th) slice, never a
 * substitute for the Nth-ranked category. The fold always happens on the
 * volume-desc order the reader returns ("Other" is the low-volume tail);
 * alpha sort is presentation only and keeps "Other" last.
 *
 * "Other" is one more slice of the whole to a pie or a proportion bar, but it
 * is NOT a rank to a ranked list: the tail routinely outweighs every named
 * category, so a ranking splits it out (`OTHER_SLICE_ID`) and shows it as a
 * footing outside the rows.
 *
 * `fn` is the slices' measure: the tail's figure folds by it (a sum adds, an
 * average re-weights by project count, an extreme takes the extreme — see
 * `foldMeasure`), and so does each entry of a slice's `values`. Null is the
 * plain count.
 */
export function shapeSlices(
  data: DimensionSlice[],
  sort: SliceSort,
  show: SliceShow,
  fn: MeasureFn | null = null,
): DimensionSlice[] {
  const max = SHOW_LIMIT[show];
  let shaped = data;
  if (data.length > max) {
    const head = data.slice(0, max);
    const tail = data.slice(max);
    const part = (d: DimensionSlice, value: number | null | undefined) => ({
      total: d.count ?? d.total,
      count: d.count ?? d.total,
      value: value ?? null,
    });
    const other = foldMeasure(fn, tail.map((d) => part(d, d.total))) ?? 0;
    const count = tail.reduce((sum, d) => sum + (d.count ?? d.total), 0);
    const width = Math.max(0, ...tail.map((d) => d.values?.length ?? 0));
    const values =
      width > 0
        ? Array.from({ length: width }, (_, index) =>
            foldMeasure(fn, tail.map((d) => part(d, d.values?.[index] ?? null))),
          )
        : undefined;
    // "Other" is a mixed tail, not an entity — it always wears the muted grey,
    // never a ramp hue that would read as one more category.
    const folded: DimensionSlice = { id: OTHER_SLICE_ID, label: "Other", total: other, count, color: "grey" };
    shaped = [...head, values ? { ...folded, values } : folded];
  }
  if (sort === "alpha") {
    const named = shaped.filter((s) => s.id !== OTHER_SLICE_ID);
    const other = shaped.filter((s) => s.id === OTHER_SLICE_ID);
    shaped = [...named.sort((a, b) => a.label.localeCompare(b.label)), ...other];
  }
  return shaped;
}

/**
 * The full read→shape→color plumbing for a slice widget in one call: the shared
 * reader over `groupBy`, the Show/Sort fold, and THE categorical assignment
 * (`sliceColors`, index-aligned with `slices`) — entity-owned palette color
 * first, ramp fallback for colorless slices, "Other" muted. Marks take
 * `sliceColors.tokens[i]`, series accessors `sliceColors.colors[i]` or
 * `.scale(label)`, legends `.scale` — so segments, wedges, bars, and legends
 * all color alike AND a stage keeps its color across every widget. Takes the
 * widget's config directly — every slice widget's schema carries the
 * `SliceConfig` keys plus `sort`/`show`. `data` is the unfolded set (for
 * `sliceDrilldownIds`); `dimension` is the resolved one a drilldown slice
 * names; `measure` is how the widget prints its whole and `measures` how each
 * field prints (one per surviving field); `pending` means the Measure names a
 * field the workspace no longer has, so the card shows a prompt instead of an
 * empty chart. Rendering the slices stays the widget's own.
 *
 * `compareFields` (the bar's Compare with row) keeps the Group by and carries
 * every field's figure on each slice's `values` — a bar per field inside each
 * stage; `total` stays the Measure field's.
 */
export function useShapedSlices(config: SliceConfig & {
  sort: SliceSort;
  show: SliceShow;
  filters?: unknown;
  dateField?: "creationDate" | "lastUpdateDate";
  /** One color for every slice that owns none, in place of the cycling ramp —
   *  for a widget whose rows are separated by their label, not by hue. */
  fallbackToken?: ChartColorToken;
}): {
  data: DimensionSlice[];
  slices: DimensionSlice[];
  sliceColors: CategoricalColors;
  dimension: WidgetDimension | null;
  measure: MeasureFormat;
  measures: MeasureFormat[];
  pending: boolean;
  loading: boolean;
  error: unknown;
} {
  const { asked, pending: measurePending } = useFieldMeasures(
    config.measure,
    config.calculate,
    config.compareFields,
  );
  const grouped = useGroupDimension(config.groupBy);
  const pending = grouped === null || measurePending;
  const dimension = pending ? null : grouped;
  const { data, loading, error } = useDimensionSlices(
    dimension,
    config.filters,
    config.dateField,
    asked,
    config.showUnassigned ?? false,
  );
  const measure = useMeasureFormat(asked);
  const measures = useMeasureFormats(asked);
  const fn = measure.measure?.fn ?? null;
  const slices = useMemo(
    () => shapeSlices(data, config.sort, config.show, fn),
    [data, config.sort, config.show, fn],
  );
  const sliceColors = useCategoricalColors(
    slices.map((slice) => ({
      ...slice,
      preserveColor: slice.id === OTHER_SLICE_ID,
    })),
    { fallbackToken: config.fallbackToken },
  );
  return { data, slices, sliceColors, dimension, measure, measures, pending, loading, error };
}

/** The empty state of a slice widget whose Group by or Measure names a field
 *  the workspace no longer has: it says what happened and where the fix is,
 *  rather than reading as an unconfigured card. The title is the same phrase
 *  the settings select shows in place of the missing pick. */
export const MISSING_FIELD_TITLE = MISSING_FIELD_LABEL;
export const MISSING_FIELD_DESCRIPTION =
  "Pick another field in Settings.";

/**
 * The covered category ids for a (possibly folded) slice press → a widget-owned
 * drilldown: a real slice covers its own id; the "Other" wedge covers every id
 * in `all` that isn't among the `shown` head slices.
 */
export function sliceDrilldownIds(
  slice: DimensionSlice,
  all: DimensionSlice[],
  shown: DimensionSlice[],
): string[] {
  // A merged bucket is not itself a category id, so a drilldown asks for the
  // ids it stands for; every other slice IS its own id.
  const covered = (d: DimensionSlice): string[] => [...(d.ids ?? [d.id])];
  if (slice.id !== OTHER_SLICE_ID) return covered(slice);
  const head = new Set(shown.filter((s) => s.id !== OTHER_SLICE_ID).map((s) => s.id));
  return all.filter((d) => !head.has(d.id)).flatMap(covered);
}

export type { DimensionSlice, WidgetDimension };
